import { useEffect, useMemo, useRef, useState, type ImgHTMLAttributes } from "react";
import { cn } from "@/lib/utils";
import {
  getCarImageDisplayCandidates,
  type CarImageDisplayPreset,
} from "@/lib/car-image-url";
import { IMAGE_CANDIDATE_HANG_MS, shouldAdvanceHungImageCandidate } from "@/lib/supabase-session-token";
import {
  cardInstantBackground,
  rememberCardPaintUrl,
  rememberShownCardImage,
  shouldEagerLoadCarImage,
} from "@/lib/card-image-loading";

type Props = {
  src: string | null | undefined;
  alt: string;
  className?: string;
  fallbackClassName?: string;
  priority?: boolean;
  preset?: CarImageDisplayPreset;
  fallbackLabel?: string;
  /** Avatars / thumbs · no large empty-state min-height */
  compact?: boolean;
  /** Overrides the preset slot width. Use when the photo is not the preset's default size. */
  sizes?: string;
  loading?: ImgHTMLAttributes<HTMLImageElement>["loading"];
} & Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "alt" | "srcSet" | "sizes" | "loading">;

/** Car listing image with Supabase-first URL fallback and optional retina srcset. */
export function CarImage({
  src,
  alt,
  className,
  fallbackClassName,
  priority,
  preset = "full",
  fallbackLabel = "الصورة غير متوفرة",
  compact = false,
  sizes,
  loading: loadingProp,
  onLoad,
  onError,
  style,
  ...props
}: Props) {
  const candidates = useMemo(
    () => getCarImageDisplayCandidates(src, preset),
    [src, preset],
  );
  const [index, setIndex] = useState(0);
  const [skipSrcSet, setSkipSrcSet] = useState(false);
  const [failed, setFailed] = useState(false);
  const [pixelsReady, setPixelsReady] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const onLoadRef = useRef(onLoad);
  const loadedSrcRef = useRef<string | null>(null);

  useEffect(() => {
    onLoadRef.current = onLoad;
  }, [onLoad]);

  useEffect(() => {
    setIndex(0);
    setSkipSrcSet(false);
    setFailed(false);
    setPixelsReady(false);
    loadedSrcRef.current = null;
  }, [src, preset]);

  const current = candidates[index];
  const activeSrcSet = skipSrcSet ? undefined : current?.srcSet;
  const eager = shouldEagerLoadCarImage({ src: current?.src ?? src, preset, priority });

  useEffect(() => {
    const img = imgRef.current;
    if (!img || !current?.src || failed) return;

    const notifyIfReady = () => {
      if (img.complete && img.naturalWidth > 0) {
        loadedSrcRef.current = current.src;
        setPixelsReady(true);
        rememberCardPaintUrl(src, img.currentSrc || current.src);
        rememberShownCardImage(current.src);
        rememberShownCardImage(src);
        onLoadRef.current?.({ currentTarget: img } as React.SyntheticEvent<HTMLImageElement>);
      }
    };

    notifyIfReady();
    img.addEventListener("load", notifyIfReady);
    return () => img.removeEventListener("load", notifyIfReady);
  }, [current?.src, failed, index, skipSrcSet]);

  useEffect(() => {
    if (!current?.src || failed) return;
    const isLocalPath = current.src.startsWith("/") && !current.src.startsWith("//");
    if (!isLocalPath) return;
    const started = Date.now();
    const id = window.setTimeout(() => {
      if (loadedSrcRef.current === current.src) return;
      if (!shouldAdvanceHungImageCandidate(Date.now() - started, IMAGE_CANDIDATE_HANG_MS)) return;
      if (current.srcSet && !skipSrcSet) {
        setSkipSrcSet(true);
        return;
      }
      if (index + 1 < candidates.length) {
        setIndex((i) => i + 1);
        setSkipSrcSet(false);
      }
    }, IMAGE_CANDIDATE_HANG_MS);
    return () => window.clearTimeout(id);
  }, [candidates.length, current?.src, current?.srcSet, failed, index, skipSrcSet]);

  if (!current?.src || failed) {
    return (
      <div
        className={cn(
          "flex size-full items-center justify-center bg-muted text-center text-sm text-muted-foreground",
          !compact && "min-h-[120px]",
          fallbackClassName,
          className,
        )}
      >
        {fallbackLabel}
      </div>
    );
  }

  const markPainted = (img: HTMLImageElement) => {
    loadedSrcRef.current = current.src;
    setPixelsReady(true);
    rememberCardPaintUrl(src, img.currentSrc || current.src);
    rememberShownCardImage(current.src);
    rememberShownCardImage(src);
  };

  const tilePreset = preset === "card" || preset === "thumb";
  const paintBg = tilePreset ? cardInstantBackground(src, current.src) : undefined;
  const image = (
    <img
      {...props}
      ref={imgRef}
      key={`${index}:${skipSrcSet ? "plain" : "full"}:${current.src}`}
      src={current.src}
      srcSet={activeSrcSet}
      sizes={activeSrcSet ? (sizes ?? current.sizes) : undefined}
      alt={alt}
      className={cn(
        !compact && tilePreset ? "car-card-photo" : undefined,
        tilePreset ? "h-full w-full" : undefined,
        !tilePreset ? className : undefined,
      )}
      style={tilePreset ? { opacity: pixelsReady ? 1 : 0 } : style}
      loading={eager ? "eager" : (loadingProp ?? "lazy")}
      decoding={tilePreset ? "sync" : "async"}
      fetchPriority={priority ? "high" : "auto"}
      draggable={false}
      onLoad={(event) => {
        markPainted(event.currentTarget);
        onLoad?.(event);
      }}
      onError={(event) => {
        if (current.srcSet && !skipSrcSet) {
          setSkipSrcSet(true);
          return;
        }
        if (index + 1 < candidates.length) {
          setIndex((i) => i + 1);
          setSkipSrcSet(false);
          return;
        }
        if (import.meta.env.DEV) {
          console.warn("[CarImage] failed to load:", src, candidates);
        }
        setFailed(true);
        onError?.(event);
      }}
    />
  );

  if (!tilePreset) {
    return image;
  }

  return (
    <span
      className={cn("relative block overflow-hidden bg-neutral-100", className)}
      style={{
        ...(paintBg
          ? {
              backgroundImage: paintBg,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
          : undefined),
        ...style,
      }}
    >
      {image}
    </span>
  );
}
